import { DashboardState, LeftPanelType, RightPanelType } from "@kuika/kuika-cl-model";
import {
  navigateToApiManagementView,
  navigateToDatasourceView,
  navigateToDesignView,
  navigateToReportDesignerView,
  navigateToScheduledJobsView,
  navigateToWorkflowView
} from "../../designer-view-actions";
import { getSdInstance } from "../helpers";
import type { ToolHandlerGroup } from "../types";

const VIEW_MAP: Record<string, DashboardState> = {
  design: DashboardState.design,
  datasources: DashboardState.dataDiagramScheme,
  "process-automation": DashboardState.workFlow,
  "config-manager": DashboardState.configManager,
  "email-builder": DashboardState.notificationTemplate,
  "scheduled-jobs": DashboardState.scheduledJobs,
  "api-management": DashboardState.apiManagement,
  "report-designer": DashboardState.reportDesigner
};

const REVERSE_VIEW_MAP: Record<number, string> = {};
for (const [key, val] of Object.entries(VIEW_MAP)) {
  REVERSE_VIEW_MAP[val as number] = key;
}

const LEFT_PANEL_MAP: Record<string, LeftPanelType> = {
  components: LeftPanelType.Component,
  "component-tree": LeftPanelType.DisplayItemTree,
  screens: LeftPanelType.Screens,
  datasources: LeftPanelType.Datasources,
  "image-library": LeftPanelType.ImageLibrary,
  language: LeftPanelType.Language,
  "action-tree": LeftPanelType.ActionTree,
  audit: LeftPanelType.Audit
};

const REVERSE_LEFT_PANEL_MAP: Record<number, string> = {};
for (const [key, val] of Object.entries(LEFT_PANEL_MAP)) {
  REVERSE_LEFT_PANEL_MAP[val as number] = key;
}

const RIGHT_PANEL_MAP: Record<string, RightPanelType> = {
  properties: RightPanelType.Properties,
  styling: RightPanelType.Styling,
  documents: RightPanelType.Documents,
  comments: RightPanelType.Comments,
  history: RightPanelType.History,
  chatbot: RightPanelType.Chatbot
};

const REVERSE_RIGHT_PANEL_MAP: Record<number, string> = {};
for (const [key, val] of Object.entries(RIGHT_PANEL_MAP)) {
  REVERSE_RIGHT_PANEL_MAP[val as number] = key;
}

/** Which tool the designer is currently showing, in the names the MCP surface uses. */
export interface CurrentViewDescription {
  view: string;
  leftPanel: string;
  rightPanel: string;
}

/**
 * KOA-18226 — read the designer's current view.
 *
 * Extracted from `ui_common_get_current_view` so a failing write can NAME the state that
 * caused it. `dashboardState` decides where a screen action-attach is routed, it is sticky
 * across navigation, and until now nothing recorded it: a build whose attaches were all
 * silently discarded left no trace of the one value that explains why.
 */
export function describeCurrentView(sd: ReturnType<typeof getSdInstance>): CurrentViewDescription {
  const dashboardState = sd?.designSituationProvider?.state?.dashboardState ?? sd?.state?.dashboardState ?? null;
  const leftPanel = sd?.designSituationProvider?.getActiveLeftPanel?.() ?? null;
  const rightPanel = sd?.designSituationProvider?.getActiveRightPanel?.() ?? null;
  return {
    view: REVERSE_VIEW_MAP[dashboardState] || `unknown(${dashboardState})`,
    leftPanel: REVERSE_LEFT_PANEL_MAP[leftPanel] || `unknown(${leftPanel})`,
    rightPanel: REVERSE_RIGHT_PANEL_MAP[rightPanel] || `unknown(${rightPanel})`
  };
}

export const navigationTools: ToolHandlerGroup = {
  sync: {
    ui_common_get_current_view: () => {
      return describeCurrentView(getSdInstance());
    },

    ui_project_set_left_panel: (input) => {
      const { panel } = input;
      if (!panel) return { error: "panel is required" };

      const panelType = LEFT_PANEL_MAP[panel];
      if (panelType === undefined) {
        return { error: `Invalid panel "${panel}". Valid values: ${Object.keys(LEFT_PANEL_MAP).join(", ")}` };
      }

      const sd = getSdInstance();
      sd.designSituationProvider?.setActiveLeftPanelByClick?.(panelType);

      return {
        success: true,
        message: `Left panel set to "${panel}"`,
        panel
      };
    },

    ui_project_set_right_panel: (input) => {
      const { panel } = input;
      if (!panel) return { error: "panel is required" };

      const panelType = RIGHT_PANEL_MAP[panel];
      if (panelType === undefined) {
        return { error: `Invalid panel "${panel}". Valid values: ${Object.keys(RIGHT_PANEL_MAP).join(", ")}` };
      }

      const sd = getSdInstance();
      sd.designSituationProvider?.setActiveRightPanelByClick?.(panelType);

      return {
        success: true,
        message: `Right panel set to "${panel}"`,
        panel
      };
    }
  },

  async: {
    ui_project_navigate_to_view: async (input) => {
      const { view } = input;
      if (!view) return { error: "view is required" };

      const targetState = VIEW_MAP[view];
      if (targetState === undefined) {
        return { error: `Invalid view "${view}". Valid values: ${Object.keys(VIEW_MAP).join(", ")}` };
      }

      const sd = getSdInstance();
      const currentState = sd.designSituationProvider?.state?.dashboardState ?? sd.state?.dashboardState ?? null;
      if (currentState === targetState) {
        return {
          success: true,
          message: `Already on "${view}" view`,
          view
        };
      }

      if (targetState === DashboardState.design) {
        await navigateToDesignView(sd);
      } else if (targetState === DashboardState.dataDiagramScheme) {
        await navigateToDatasourceView(sd);
      } else if (targetState === DashboardState.workFlow) {
        await navigateToWorkflowView(sd, {
          setRootSelection: true
        });
      } else if (targetState === DashboardState.scheduledJobs) {
        await navigateToScheduledJobsView(sd, true);
      } else if (targetState === DashboardState.apiManagement) {
        await navigateToApiManagementView(sd);
      } else if (targetState === DashboardState.reportDesigner) {
        await navigateToReportDesignerView(sd);
      } else {
        sd.designSituationProvider?.setDashboardState?.(targetState);
      }

      return {
        success: true,
        message: `Navigated to "${view}" view`,
        view
      };
    }
  }
};
